<script setup lang="ts">
import {nextTick, onMounted, ref} from "vue";
import sanitize from "sanitize-html";
import ChatCe from "@/components/Chat.vue";
import {isScopeArray, type Scope, type Scopes} from "@/types.ts";

const props = defineProps<{
  websocketUrl: string,
  user: string,
  apiKey: string,
  initialSearch?: string,
  scopes?: string
}>()

const enum SearchState {
  None,
  Searching,
  Results,
  Error
}


const searchInput = ref<string>("");
const searchString = ref<string | null>(null);
const searchResults = ref<Record<string, any>[] | null>(null)
const searchState = ref<SearchState>(SearchState.None);
const scopesParsed = ref<Scopes>([]);
const scopesStatus = ref<Record<string, boolean>>({});
const scopesIndeterminate = ref<Record<string, boolean>>({});
const modal = ref(false);

async function startSearch(event: Event) {
  event.preventDefault();
  searchResults.value = null;
  searchState.value = SearchState.Searching;
  searchString.value = null;
  await nextTick();
  searchString.value = searchInput.value;
}

onMounted(() => {
  searchInput.value = props.initialSearch || "";
  searchString.value = props.initialSearch || null;
  if (props.scopes) {
    try {
      let lScopes = JSON.parse(props.scopes);
      if (isScopeArray(lScopes)) {
        scopesParsed.value = groupNamespaces(lScopes);
        scopesParsed.value.forEach(scope => {
          scope.level = scope.id.split(".").length - 1;
          scopesStatus.value[scope.id] = scopesStatus.value[scope.id] ?? scope.default ?? false;
          if (scope.default) {
            getChildrenForScope(scope, true).forEach((child) => {
              scopesStatus.value[child.id] = true;
            })
          }
        });
        checkIndeterminate();
      } else {
        console.error("Invalid scopes format, expected an array of scopes.");
      }
    } catch (e) {
      console.error("Failed to parse scopes:", e);
    }
  }
});

function onConnectionClosed() {
  if (searchState.value === SearchState.Searching) {
    searchState.value = SearchState.Error;
  }
}

function onError(event: Event) {
  searchState.value = SearchState.Error;
}

function getChildrenForScope(scope: Scope, allChildren: boolean = false): Scopes {
  return scopesParsed.value.filter(s => s.id.startsWith(scope.id + ".") && ((s.level === (scope.level ?? 0) + 1) || allChildren))
}

function checkIndeterminate(changedScope?: Scope) {
  if (changedScope) {
    getChildrenForScope(changedScope, true).reverse().forEach((scope) => {
      scopesStatus.value[scope.id] = scopesStatus.value[changedScope.id] ?? false;
      scopesIndeterminate.value[scope.id] = false;
    })
  }
  for (let i = scopesParsed.value.length - 1; i >= 0; i--) {
    const scope = scopesParsed.value[i];
    if (!scope) {
      continue;
    }
    const children = getChildrenForScope(scope);
    const numOfChildren = children.length;
    const numChecked = children.filter(s => scopesStatus.value[s.id]).length;
    const numIndeterminate = children.filter(s => scopesIndeterminate.value[s.id]).length;
    if (numOfChildren > 0) {
      scopesStatus.value[scope.id] = (numOfChildren === numChecked);
    }
    scopesIndeterminate.value[scope.id] = numIndeterminate > 0 || (numChecked > 0 && numChecked < numOfChildren);
  }
}

function receiveSearchResults(sr: Record<string, any>[]) {
  // console.debug(sr)
  searchState.value = SearchState.Results;
  searchResults.value = sr;
}

function groupNamespaces(scopes: Readonly<Scopes>): Scopes {
  let scopesCopy = [...scopes];

  scopesCopy = scopesCopy.sort((a, b) => {
    const aSplit = a.id.split(".");
    const bSplit = b.id.split(".");
    for (let i = 0; i < Math.max(aSplit.length, bSplit.length); i++) {
      const aPart = aSplit[i] || "";
      const bPart = bSplit[i] || "";
      if (aPart && bPart) {
        if (aPart < bPart) return -1;
        if (aPart > bPart) return 1;
      } else if (aPart) {
        return 1;
      } else {
        return -1;
      }
    }
    return 0;
  });

  return scopesCopy;
}

function checkboxStyleForLevel(level: number = 0): string {
  return `padding-left: ${level * 8}px;`;
}
</script>

<template>
  <div class="search-assistant">
    <b-form @submit="startSearch">
      <b-input-group>

        <b-button @click="modal = true" variant="outline-primary">Filter</b-button>
        <BFormInput
            id="search"
            v-model="searchInput"
            trim
            placeholder="Suche"
            required
        />
        <BButton variant="primary" type="submit" aria-label="Suchen">
          <i-bi-search/>
        </BButton>
      </b-input-group>
    </b-form>
    <BModal v-model="modal" title="Filter" ok-only scrollable>
      <div v-for="scope in scopesParsed" :key="scope.id" :style="checkboxStyleForLevel(scope.level)">
        <BFormCheckbox
            :id="'filter-checkbox-' + scope.id"
            :indeterminate="scopesIndeterminate[scope.id]"
            v-model="scopesStatus[scope.id]"
            @change="() => checkIndeterminate(scope)">
          {{ scope.title }}
        </BFormCheckbox>
      </div>
    </BModal>
    <hr>

    <chat-ce v-if="searchString" :websocketUrl="websocketUrl" :user="user" :apiKey="apiKey" :scopes="scopesStatus"
             :initialSearch="searchString" @searchResults="receiveSearchResults" @close="onConnectionClosed"
             @error="onError"></chat-ce>


    <div v-if="searchState === SearchState.Results || searchState === SearchState.Searching">
      <h2>Suchergebnisse</h2>
      <div v-if="searchResults === null" style="display: flex; justify-content: center;">
        <BSpinner variant="primary"/>
      </div>
      <div v-for="r in searchResults" class="search-result">
        <a :href="r.document.path" class="search-result-title"><strong>{{ r.document.title }}</strong></a>
        <p class="text-muted">... <span v-html="sanitize(r.highlight?.text?.snippet)"></span> ...</p>
      </div>

      <div v-if="searchResults?.length === 0">
        <p class="text-muted">Keine Ergebnisse gefunden.</p>
      </div>
    </div>
    <div v-else-if="searchState === SearchState.None" class="w-100 mt-4 text-center text-muted">
      <p>Geben Sie einen Suchbegriff ein, um die Suche zu starten.</p>
    </div>

    <BAlert variant="danger" :model-value="searchState === SearchState.Error">
      Suche fehlgeschlagen! Bitte versuchen Sie es später erneut.
    </BAlert>

  </div>
</template>
